import React from 'react'
import { Typography, makeStyles, Slider } from '@paintscout/ui'
import type { MuiTheme as Theme, SliderProps, StyleClasses } from '@paintscout/ui'
import { BUILDER_V2_INK, BUILDER_V2_MUTED_TEXT } from '../../util'

const useStyles = makeStyles<Theme, SliderOptionProps>(
  (theme) => ({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: 44
    },
    sliderWrapper: {
      flex: '1 1 auto',
      minWidth: 0
    },
    sliderRoot: {
      color: BUILDER_V2_MUTED_TEXT
    },
    label: {
      flexShrink: 0,
      fontSize: '.8125rem',
      fontWeight: 500,
      marginRight: theme.spacing(3),
      color: BUILDER_V2_INK
    },
    valueLabel: {
      flexShrink: 0,
      minWidth: 36,
      marginLeft: theme.spacing(1.5),
      textAlign: 'right',
      fontSize: '.75rem',
      fontVariantNumeric: 'tabular-nums',
      color: BUILDER_V2_MUTED_TEXT
    }
  }),
  { name: 'SliderOption' }
)

export interface SliderOptionProps {
  classes?: StyleClasses<typeof useStyles>
  marks?: SliderProps['marks']
  label: string
  value: number
  min: number
  max: number
  // when provided, renders a continuous, controlled slider instead of the default
  // uncontrolled/marks-only mode -- see the "Image Dimming" control in GeneralOptions.tsx (#23100)
  step?: number
  /** Shows the current value after the slider (continuous mode only), e.g. `(v) => `${v * 100}%`` */
  formatValue?: (value: number) => string
  onChange: (value: number) => void
}

function SliderOption(props: SliderOptionProps) {
  const classes = useStyles(props)
  const { marks, label, value, min, max, step, formatValue, onChange } = props
  const isContinuous = step != null

  const handleChange: any = (event: React.ChangeEvent<HTMLSpanElement>, value: number) => {
    onChange(value)
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.label} variant={'h5'}>
        {label}
      </Typography>
      <div className={classes.sliderWrapper}>
        <Slider
          onMouseDown={(event) => event.stopPropagation()}
          classes={{ root: classes.sliderRoot }}
          value={isContinuous ? value : undefined}
          defaultValue={isContinuous ? undefined : value}
          marks={marks}
          max={max}
          min={min}
          onChange={handleChange}
          step={isContinuous ? step : null}
          track={isContinuous ? undefined : false}
        />
      </div>
      {isContinuous && formatValue && <Typography className={classes.valueLabel}>{formatValue(value)}</Typography>}
    </div>
  )
}

export default SliderOption
